import { useMemo } from "react";

import {
  INHERIT_BUDGET_MIX_KPI,
  useMMMBudgetMixKpiId,
} from "@features/mmm/hooks/mmm-url-state";
import { getFractionChanged } from "@features/mmm/utils/calculators";
import { calculateCac, calculateRoas } from "@north-beam/nb-common";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";

import { KpiType } from "../../client-setting/types";
import { type HistoricalMetricComparisonData } from "../card";

import { MainTitleSectionContainer } from "./main-title-section";
import { PercentageChangeChip } from "./metric-box";
import { TitleSectionContainer } from "./title-section-container";

type RangedData = {
  low: number;
  median: number;
  high: number;
};

type KpiData = {
  kpi_id: number;
  values: RangedData;
};

export function ErrorTitleSection({
  historicalCompareData,
  title,
  kpiData,
  spend,
}: {
  historicalCompareData?: HistoricalMetricComparisonData;
  title: string;
  kpiData: Record<string, KpiData>;
  spend: number;
}) {
  const { selectedBudgetMixKpiId } = useMMMBudgetMixKpiId();
  const { kpis: clientKpis } = useClientKpis();

  const kpiMetric = useMemo(() => {
    if (selectedBudgetMixKpiId === INHERIT_BUDGET_MIX_KPI) {
      return clientKpis.find((kpi) => kpi.is_default_kpi);
    }

    return clientKpis.find((kpi) => kpi.id === selectedBudgetMixKpiId);
  }, [selectedBudgetMixKpiId, clientKpis]);

  const compareData: {
    metric: string;
    metricChangePercentage: number;
    shouldError: boolean;
    isDesiredDirection: boolean;
  } = useMemo(() => {
    const historicalValue =
      historicalCompareData?.kpi_data &&
      historicalCompareData.kpi_data[kpiMetric?.kpi_name || ""];
    const rangedData = kpiData[kpiMetric?.kpi_name || ""];
    const historicalSpend = historicalCompareData?.spend;
    const shouldError =
      !historicalCompareData ||
      !historicalSpend ||
      !historicalValue ||
      !rangedData;

    if (kpiMetric?.kpi_type === KpiType.REVENUE) {
      if (shouldError) {
        return {
          metric: "roas",
          metricChangePercentage: 0,
          shouldError: true,
          isDesiredDirection: false,
        };
      }
      const currentValue = rangedData.values.median;
      const historicalRoas = calculateRoas({
        revenue: historicalValue,
        spend: historicalSpend,
      });
      const currentRoas = calculateRoas({ revenue: currentValue, spend });
      const metricChangePercentage =
        getFractionChanged({
          current: currentRoas,
          historical: historicalRoas,
        }) * 100;
      return {
        metric: "roas",
        metricChangePercentage,
        isDesiredDirection: metricChangePercentage > 0,
        shouldError: false,
      };
    } else {
      if (shouldError) {
        return {
          metric: "cac",
          metricChangePercentage: 0,
          shouldError: true,
          isDesiredDirection: false,
        };
      }
      const currentValue = rangedData.values.median;
      const historicalCac = calculateCac({
        transactions: historicalValue,
        spend: historicalSpend,
      });
      const currentCac = calculateCac({ transactions: currentValue, spend });
      const metricChangePercentage =
        getFractionChanged({
          current: currentCac,
          historical: historicalCac,
        }) * 100;
      return {
        metric: "cac",
        metricChangePercentage,
        shouldError: false,
        isDesiredDirection: metricChangePercentage < 0,
      };
    }
  }, [
    historicalCompareData,
    kpiData,
    kpiMetric?.kpi_name,
    kpiMetric?.kpi_type,
    spend,
  ]);

  return (
    <TitleSectionContainer>
      {compareData && <PercentageChangeChip compareData={compareData} />}
      <MainTitleSectionContainer>
        <span className="font-semibold">{title}</span>
        <span className="text-mars font-semibold">
          ERROR Could not find client setting for budget
        </span>
      </MainTitleSectionContainer>
    </TitleSectionContainer>
  );
}
